import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertCustomComponent } from 'pharedata-ng-components';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LlamanosApiService } from '../../../services/api-llamanos.service';
import {
  getFieldKey,
  isAllowedEvidenceFile,
  ALLOWED_EVIDENCE_ACCEPT,
  ALLOWED_EVIDENCE_FORMATS_LABEL,
  uploadEvidenceWithPresignedUrl,
} from '../../../utils/utils';
import { getChileTimeZone } from '../../../utils/timeZone';

export interface ApelacionData {
  caseNumber: string;
  caso: any;
  categoriaType: string;
  categoria: string;
  subcategoria: any;
  displayFields: any[];
}

@Component({
  selector: 'app-apelacion',
  standalone: true,
  imports: [CommonModule, FormsModule, AlertCustomComponent],
  templateUrl: 'apelacion.component.html',
  styleUrls: ['apelacion.component.scss'],
})
export class ApelacionComponent {
  nombres: string = '';
  apellidos: string = '';
  email: string = '';
  telefono: string = '';
  observaciones: string = '';
  canSubmit: boolean = false;

  // Estado para lib-custom-alert
  hasError: boolean = false;
  showAlert: boolean = false;
  alertTitle = '';
  alertContent = '';
  alertIcon = '';
  numeroCaso: string = '';
  wasSuccess: boolean = false;

  private caso: any;
  private categoriaType: string;
  private categoria: string;
  private subcategoria: any;
  private displayFields: any[];

  selectedEvidenceFile: File | null = null;
  selectedEvidenceName: string = '';
  protected readonly allowedEvidenceAccept = ALLOWED_EVIDENCE_ACCEPT;
  protected readonly allowedEvidenceFormatsLabel =
    ALLOWED_EVIDENCE_FORMATS_LABEL;

  constructor(
    public dialogRef: MatDialogRef<ApelacionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApelacionData,
    private api: LlamanosApiService,
  ) {
    this.caso = data?.caso;
    this.categoriaType = data?.categoriaType ?? '';
    this.categoria = data?.categoria ?? '';
    this.subcategoria = data?.subcategoria;
    this.displayFields = data?.displayFields ?? [];
  }

  close(): void {
    this.dialogRef.close();
  }

  closeAlert = () => {
    this.showAlert = false;
    if (this.hasError) {
      this.hasError = false;
      return;
    }

    if (this.wasSuccess) {
      this.dialogRef.close({ success: true, numeroCaso: this.numeroCaso });
    }
  };

  // No-op helpers to cumplir con la API de lib-custom-alert
  hideGreyBackground = () => {};
  showGreyBackground = () => {};
  scrollTo = (_x: number, _y: number) => {};

  onAttachEvidenceClick(input: HTMLInputElement): void {
    input.click();
  }

  onEvidenceSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files.length > 0 ? input.files[0] : null;
    if (!file) return;

    const maxBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxBytes) {
      alert('El archivo es demasiado grande. El máximo permitido es 10MB.');
      this.selectedEvidenceFile = null;
      this.selectedEvidenceName = '';
      input.value = '';
      return;
    }

    if (!isAllowedEvidenceFile(file)) {
      alert(
        `Solo se permiten archivos con formato ${this.allowedEvidenceFormatsLabel}.`,
      );
      this.selectedEvidenceFile = null;
      this.selectedEvidenceName = '';
      input.value = '';
      return;
    }

    this.selectedEvidenceFile = file;
    this.selectedEvidenceName = file.name || '';
  }

  removeFile(): void {
    this.selectedEvidenceFile = null;
    this.selectedEvidenceName = '';
  }

  updateCanSubmit(): void {
    const emailRegex = /^[\w-.]+@[\w-]+\.[A-Za-z]{2,}$/;
    const phoneRegex = /^[0-9]{8}$/;

    if (
      this.nombres.length == 0 ||
      this.apellidos.length == 0 ||
      !emailRegex.test(this.email) ||
      !phoneRegex.test(this.telefono) ||
      this.observaciones.length === 0
    ) {
      this.canSubmit = false;
      return;
    }
    this.canSubmit = true;
  }

  async reinsistir(): Promise<void> {
    const body: any = {};
    const parametros: any[] = [];

    for (const field of this.displayFields) {
      const key = field.field_name;
      let contenido = this.caso[key];

      if (contenido === undefined || contenido === null) continue;

      parametros.push({
        campo: key,
        contenido: contenido,
      });
    }

    body['eds'] = this.caso.EDSCodigo__c;
    body['categoria'] = this.caso.Categoria1__c;
    body['subcategoria'] = this.caso.Categoria2__c;
    body['parametros'] = parametros;
    body['cliente'] = {
      nombres: this.nombres,
      apellidos: this.apellidos,
      email: this.email,
      telefono: '+569' + this.telefono,
    };
    body['descripcion'] = this.observaciones;
    body['CaseNumber'] = this.caso.CaseNumber;
    body['reinsistir'] = true;

    let presignedKey: string | null = null;
    if (this.selectedEvidenceFile) {
      const nombreArchivo =
        this.selectedEvidenceName ||
        this.selectedEvidenceFile.name ||
        'archivo';
      try {
        presignedKey = await uploadEvidenceWithPresignedUrl({
          file: this.selectedEvidenceFile,
          fileName: nombreArchivo,
          getPresignedUrl: (n, t) => this.api.getPresignedUrl(n, t),
          uploadFileToPresignedUrl: (u, f) =>
            this.api.uploadFileToPresignedUrl(u, f),
        });
      } catch (err) {
        alert(
          err instanceof Error
            ? err.message
            : 'Ha ocurrido un error subiendo el archivo adjunto.',
        );
        return;
      }
    }

    if (this.selectedEvidenceFile && presignedKey) {
      const filename =
        this.selectedEvidenceName ||
        this.selectedEvidenceFile.name ||
        'archivo';
      const dotIndex = filename.lastIndexOf('.');
      const titulo = dotIndex > 0 ? filename.substring(0, dotIndex) : filename;
      body['archivo'] = {
        nombre: filename,
        titulo,
        s3Key: presignedKey,
        key: presignedKey,
      };
    }

    this.api.crearCaso(body).subscribe({
      next: (res: any) => {
        console.log('Mostrando alerta!!');
        this.numeroCaso = res.case?.numero_caso || '';
        this.alertTitle = '¡Operación exitosa!';
        this.alertContent =
          'La apelación ha sido ingresada con éxito. N° de Caso: ' +
          this.numeroCaso;
        this.alertIcon = 'success';
        this.showAlert = true;
        this.hasError = false;
        this.wasSuccess = true;
      },
      error: (err: unknown) => {
        if (typeof err === 'object' && err !== null && 'error' in err) {
          const e = err as { error?: any };
          this.alertContent =
            'Ha ocurrido un error creando el caso. ' +
            (e.error?.case?.error ?? '');
        } else {
          this.alertContent = 'Ha ocurrido un error al ingresar la apelación.';
        }

        this.numeroCaso = '';
        this.alertTitle = 'Error';
        this.alertIcon = 'error';
        this.showAlert = true;
        this.hasError = true;
        this.wasSuccess = false;
      },
    });
  }
}
